import {
  Box, Button, Divider, Grid2, List, ListItem, ListItemAvatar, ListItemText, Typography, useTheme,
} from '@mui/material';
import PropTypes from 'prop-types';
import LanguagePopover from '@components/organisms/LanguagePopover';
import React, {
  forwardRef, useCallback, useEffect, useRef, useState,
} from 'react';
import { trackEvent } from '@components/organisms/Tracking';
import {
  FiChevronLeft, FiMapPin, FiPhoneCall, FiTool,
} from 'react-icons/fi';
import { LinkElement } from '@plugins/next-cms-core';
import Link from 'next/link';
import { useTranslation } from 'next-i18next';
import Image from '@components/atoms/Image';
import Image2 from '@components/atoms/Image2';
import MobileMenuItem from './MobileMenuItem';
import MenuItemBadge from './MenuItemBadge';
import { navigationItemShape } from './propTypes';
import { IconCarSearch } from '../../organisms/FloatingActionBar';

const SLIDE_DURATION = 300;

function MobileMenu(props, ref) {
  const {
    items,
    onClick,
    locales,
    pageUrls,
    siteConfig,
  } = props;
  const { t } = useTranslation();
  const [openPopover, setOpenPopover] = useState(false);
  const [navStack, setNavStack] = useState([{
    title: null,
    items,
  }]);
  const [slideDirection, setSlideDirection] = useState(null); // 'left' | 'right' | null
  const [isAnimating, setIsAnimating] = useState(false);
  const timeoutRef = useRef(null);

  // Reset stack when root items change
  useEffect(() => {
    setNavStack([{
      title: null,
      items,
    }]);
    setSlideDirection(null);
  }, [items]);

  // Clean up timeout on unmount
  useEffect(() => () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
  }, []);

  const currentLevel = navStack[navStack.length - 1];

  const handleDrillDown = useCallback((item) => {
    if (isAnimating) return;
    setSlideDirection('left');
    setIsAnimating(true);

    const hasMegaMenu = item.megaMenu
      && (item.megaMenu.groups?.length > 0 || item.megaMenu.items?.length > 0);

    timeoutRef.current = setTimeout(() => {
      if (hasMegaMenu) {
        // Flatten all groups into a single level with group titles as headers
        const sections = [];
        item.megaMenu.groups?.forEach((group) => {
          sections.push({
            type: 'group',
            group,
          });
        });
        if (item.megaMenu.items?.length > 0) {
          sections.push({
            type: 'ungrouped',
            items: item.megaMenu.items,
          });
        }
        setNavStack((prev) => [...prev, {
          title: item.title,
          megaMenuSections: sections,
        }]);
      } else {
        setNavStack((prev) => [...prev, {
          title: item.title,
          items: item.items,
        }]);
      }
      setSlideDirection(null);
      setIsAnimating(false);
    }, SLIDE_DURATION);
  }, [isAnimating]);

  const handleBack = useCallback(() => {
    if (isAnimating || navStack.length <= 1) return;
    setSlideDirection('right');
    setIsAnimating(true);
    timeoutRef.current = setTimeout(() => {
      setNavStack((prev) => prev.slice(0, -1));
      setSlideDirection(null);
      setIsAnimating(false);
    }, SLIDE_DURATION);
  }, [isAnimating, navStack.length]);

  const handleClosePopover = () => {
    setOpenPopover(false);
    onClick();
  };
  const handleOpen = () => {
    setOpenPopover(true);
  };

  // eslint-disable-next-line no-nested-ternary
  const slideTransform = slideDirection === 'left'
    ? 'translateX(-100%)'
    : slideDirection === 'right'
      ? 'translateX(100%)'
      : 'translateX(0)';

  return (
    <List ref={ref} component="nav" disablePadding>
      <Box
        sx={{
          bgcolor: 'quaternary.main',
          pt: 2,
          px: 2,
          pb: '1px',
        }}
      >
        <LanguagePopover
          handleOpen={handleOpen}
          locales={locales ?? []}
          onClose={handleClosePopover}
          open={openPopover}
          pageUrls={pageUrls}
        />
        <MobileMenuActions
          onClick={onClick}
          siteConfig={siteConfig}
          t={t}
        />
      </Box>
      <Box sx={{
        overflow: 'hidden',
        px: 1,
        pt: 1,
      }}
      >
        <Box
          sx={{
            transform: slideTransform,
            transition: slideDirection
              ? `transform ${SLIDE_DURATION}ms cubic-bezier(0.4, 0, 0.2, 1)`
              : 'none',
          }}
        >
          {navStack.length > 1 && (
            <ListItem
              button
              onClick={handleBack}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                py: 1.5,
              }}
            >
              <FiChevronLeft size={20} />
              <Typography component="span">
                {t('components.molecules.MainMenu.MobileMenu.back')}
              </Typography>
            </ListItem>
          )}
          {currentLevel.items && currentLevel.items.map((item) => (
            <MobileMenuItem
              key={item.path}
              item={item}
              onClick={onClick}
              onDrillDown={handleDrillDown}
            />
          ))}
          {currentLevel.megaMenuSections && currentLevel.megaMenuSections.map((section, index) => {
            if (section.type === 'group') {
              const { group } = section;
              const isLast = index >= currentLevel.megaMenuSections.length - 1;
              return (
                <Box key={group.id} className={group.cssClass} mb={1}>
                  <ListItem
                    sx={{
                      pt: 2,
                      pb: 0.5,
                    }}
                  >
                    <Typography
                      component="span"
                      sx={{
                        fontWeight: 700,
                        color: 'text.secondary',
                      }}
                      variant="overline"
                    >
                      {group.title}
                    </Typography>
                  </ListItem>
                  {group.items?.map((menuItem) => {
                    const image = menuItem.image?.formats?.thumbnail
                      ?? menuItem.image?.formats?.small
                      ?? menuItem.image;
                    return (
                      <LinkElement key={menuItem.id} data={menuItem.link} onClick={onClick}>
                        <ListItem button className={menuItem.cssClass} component="a" onClick={onClick} sx={{ gap: 2 }}>
                          {image && (
                            <ListItemAvatar>
                              <Image2
                                alt={menuItem.title}
                                height={image.height ?? 100}
                                src={image.url}
                                style={{
                                  display: 'block',
                                  width: (image.width && image.height) ? 70 : 40,
                                  height: 'auto',
                                }}
                                width={image.width ?? 100}
                              />
                            </ListItemAvatar>
                          )}
                          <ListItemText>
                            <Typography component="span">
                              {menuItem.title}
                            </Typography>
                          </ListItemText>
                          {menuItem.badge && (
                            <MenuItemBadge
                              icon={menuItem.badgeIcon}
                              label={menuItem.badge}
                            />
                          )}
                        </ListItem>
                      </LinkElement>
                    );
                  })}
                  {!isLast && <Divider sx={{ mt: 1 }} />}
                </Box>
              );
            }
            // ungrouped items
            return section.items?.map((menuItem) => {
              const image = menuItem.image?.formats?.thumbnail
                ?? menuItem.image?.formats?.small
                ?? menuItem.image;
              return (
                <LinkElement key={menuItem.id} data={menuItem.link} onClick={onClick}>
                  <ListItem button className={menuItem.cssClass} component="a" onClick={onClick} sx={{ gap: 2 }}>
                    {image && (
                      <ListItemAvatar>
                        <Image2
                          alt={menuItem.title}
                          height={image.height ?? 100}
                          src={image.url}
                          style={{
                            display: 'block',
                            width: (image.width && image.height) ? 70 : 40,
                            height: 'auto',
                          }}
                          width={image.width ?? 100}
                        />
                      </ListItemAvatar>
                    )}
                    <ListItemText>
                      <Typography component="span">
                        {menuItem.title}
                      </Typography>
                    </ListItemText>
                    {menuItem.badge && (
                      <MenuItemBadge
                        icon={menuItem.badgeIcon}
                        label={menuItem.badge}
                      />
                    )}
                  </ListItem>
                </LinkElement>
              );
            });
          })}
        </Box>
      </Box>
      <MobileSponsors onClick={onClick} />
    </List>
  );
}

const MobileMenuWithRef = forwardRef(MobileMenu);

MobileMenuWithRef.propTypes = {
  items: PropTypes.arrayOf(navigationItemShape),
  // Events
  onClick: PropTypes.func,
};

export default MobileMenuWithRef;

const iconStyles = {
  display: 'block',
  width: '24px',
  height: '24px',
  marginTop: '8px',
  marginBottom: '0px',
};

function MobileMenuActions(props) {
  const {
    onClick,
    t,
    siteConfig,
  } = props;
  const {
    sidebarPhone,
    sidebarUrlLocations,
    sidebarUrlWorkshop,
    sidebarUrlCarsearch,
  } = siteConfig ?? {};

  const theme = useTheme();

  return (
    <Box
      sx={{
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        my: 2,
      }}
    >
      <Link href={sidebarUrlWorkshop ?? '#'} legacyBehavior>
        <Button
          aria-label={t('components.organisms.FloatingActionBar.workshop')}
          color="default"
          component="a"
          onClick={() => {
            onClick();
            trackEvent('Contact');
            trackEvent('ContactCustom', {
              channel: 'WorkshopAppointment',
              origin: 'Floating Action Bar',
            });
          }}
          sx={{
            flexDirection: 'column',
            color: theme.palette.primary.contrastText,
            backgroundColor: theme.palette.primary.light,
            '&:hover': {
              backgroundColor: theme.palette.primary.main,
              color: theme.palette.primary.contrastText,
            },
          }}
        >
          <FiTool
            style={iconStyles}
          />
          <Typography variant="caption">
            {t('components.organisms.FloatingActionBar.workshop')}
          </Typography>
        </Button>
      </Link>
      <Link href={sidebarUrlCarsearch ?? '#'} legacyBehavior>
        <Button
          aria-label={t('components.organisms.FloatingActionBar.vehicles')}
          color="default"
          component="a"
          onClick={() => {
            onClick();
            trackEvent('Contact');
            trackEvent('ContactCustom', {
              channel: 'Vehicles',
              origin: 'Floating Action Bar',
            });
          }}
          sx={{
            flexDirection: 'column',
            '&:hover': {
              backgroundColor: theme.palette.primary.main,
              color: theme.palette.primary.contrastText,
            },
          }}
        >
          <IconCarSearch />
          <Typography variant="caption">
            {t('components.organisms.FloatingActionBar.vehicles')}
          </Typography>
        </Button>
      </Link>
      <Link href={`tel:${sidebarPhone}`} legacyBehavior>
        <Button
          aria-label={t('components.organisms.FloatingActionBar.call')}
          color="default"
          component="a"
          onClick={() => {
            onClick();
            trackEvent('Contact');
            trackEvent('ContactCustom', {
              channel: 'Phone',
              origin: 'Floating Action Bar',
            });
          }}
          sx={{
            flexDirection: 'column',
            '&:hover': {
              backgroundColor: theme.palette.primary.main,
              color: theme.palette.primary.contrastText,
            },
          }}
        >
          <FiPhoneCall
            style={iconStyles}
          />
          <Typography variant="caption">
            {t('components.organisms.FloatingActionBar.call')}
          </Typography>
        </Button>
      </Link>
      <Link href={sidebarUrlLocations ?? '#'} legacyBehavior>
        <Button
          aria-label={t('components.organisms.FloatingActionBar.locations')}
          color="default"
          component="a"
          onClick={() => {
            onClick();
            trackEvent('Contact');
            trackEvent('ContactCustom', {
              channel: 'Branches',
              origin: 'Floating Action Bar',
            });
          }}
          sx={{
            flexDirection: 'column',
            '&:hover': {
              backgroundColor: theme.palette.primary.main,
              color: theme.palette.primary.contrastText,
            },
          }}
        >
          <FiMapPin
            style={iconStyles}
          />
          <Typography variant="caption">
            {t('components.organisms.FloatingActionBar.locations')}
          </Typography>
        </Button>
      </Link>
    </Box>
  );
}

function MobileSponsors({ onClick }) {
  return (
    <Grid2
      container
      spacing={2}
      sx={{
        mt: 5,
        px: 2,
        alignItems: 'center',
        justifyContent: 'space-around',
      }}
    >
      <Grid2 size={{ xs: 2 }}>
        <Link href="/hyundai" onClick={onClick}>
          <Image
            alt="Hyundai"
            isFluid
            src="/images/car-brands/hyundai-motor-company-logo-2.svg"
            styles={{
              width: 145,
              filter: 'grayscale(1) brightness(2)',
            }}
          />
        </Link>
      </Grid2>
      <Grid2 size={{ xs: 2 }}>
        <Link href="/mitsubishi" onClick={onClick}>
          <Image
            alt="Mitsubishi"
            isFluid
            src="/images/car-brands/mitsubishi-logo.svg"
            styles={{
              width: 145,
              filter: 'grayscale(1) brightness(2)',
            }}
          />
        </Link>
      </Grid2>
      <Grid2 size={{ xs: 2 }}>
        <Link href="/fuso" onClick={onClick}>
          <Image
            alt="FUSO"
            isFluid
            src="/images/car-brands/mitsubishi-fuso-logo.svg"
            styles={{
              width: 145,
              filter: 'grayscale(1) brightness(2)',
            }}
          />
        </Link>
      </Grid2>
      <Grid2 size={{ xs: 2 }}>
        <Link href="/maxus" onClick={onClick}>
          <Image
            alt="Maxus"
            isFluid
            src="/images/car-brands/maxus-logo.png"
            styles={{
              width: 145,
              filter: 'none',
            }}
          />
        </Link>
      </Grid2>
    </Grid2>
  );
}
